import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
// import "./HRChatbot.css";
import API_BASE_URL from "../config";

const HRChatbot = () => {
  const messagesEndRef = useRef(null);
  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  // Persist userId
  const storedUserId = sessionStorage.getItem("userId");
  const currentUserId = storedUserId || `user_${Date.now()}`;
  if (!storedUserId) sessionStorage.setItem("userId", currentUserId);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Fetch messages
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE_URL}/api/chat/${currentUserId}`
        );
        setMessages(data);
      } catch (err) {
        console.error("Fetch error:", err);
      }
    };

    if (isChatOpen) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [currentUserId, isChatOpen]);

  useEffect(scrollToBottom, [messages]);

  // Send a message
  const sendMessage = async () => {
    if (!input.trim()) return;
    
    const userMessage = {
      _id: `temp-${Date.now()}`,
      sender: "user",
      text: input,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsTyping(true);
    
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/chat`, {
        userId: currentUserId,
        text: input,
      });
      
      setMessages(prev => prev.map(m => m._id === userMessage._id ? data : m));
      setIsTyping(false);
    } catch (err) {
      console.error("Send error:", err);
      setIsTyping(false);
    }
  };

  // Delete single message
  const deleteMessage = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/chat/message/${id}`);
      setMessages((prev) => prev.filter((m) => m._id !== id));
    } catch (err) {
      console.error("Delete message error:", err);
    }
  };

  // Clear entire chat
  const clearChat = async () => {
    if (!window.confirm("Clear entire chat?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/chat/clear/${currentUserId}`);
      setMessages([]);
    } catch (err) {
      console.error("Clear chat error:", err);
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      <div className={`hr-chatbot-floating-btn ${isChatOpen ? 'active' : ''}`} onClick={() => setIsChatOpen(!isChatOpen)}>
        <i className={`fas ${isChatOpen ? 'fa-times' : 'fa-comments'}`}></i>
        <span className="hr-chatbot-notification"></span>
      </div>

      {/* Chat Container */}
      <div className={`hr-chatbot-wrapper ${isChatOpen ? 'active' : ''}`}>
        <div className="hr-chatbot-container">
          <div className="hr-chatbot-header">
            <div className="hr-chatbot-avatar">
              <img 
                src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&h=100&q=80" 
                alt="HR Assistant" 
              />
            </div>
            <div className="hr-chatbot-info">
              <h3 className="hr-chatbot-title">HR Assistant</h3>
              <p className="hr-chatbot-status">{isTyping ? "Typing..." : "Online"}</p>
            </div>
            <button className="hr-chatbot-clear-btn" onClick={clearChat}>
              <i className="fas fa-trash-alt"></i>
            </button>
          </div>

          <div className="hr-chatbot-messages">
            {messages.length === 0 && (
              <div className="hr-chatbot-welcome">
                <div className="hr-chatbot-welcome-content">
                  <h2 className="gradient-text">Hello! I'm your HR Assistant</h2>
                  <p>How can I help you today?</p>
                  <div className="hr-chatbot-suggestions">
                    <button onClick={() => setInput("What are the working hours?")}>
                      Working hours?
                    </button>
                    <button onClick={() => setInput("How to request time off?")}>
                      Request time off
                    </button>
                    <button onClick={() => setInput("Benefits information")}>
                      Benefits info
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {messages.map((msg) => (
              <div
                key={msg._id}
                className={`hr-chatbot-message ${msg.sender === "user" ? "user-message" : "hr-message"}`}
                onContextMenu={(e) => {
                  e.preventDefault();
                  deleteMessage(msg._id);
                }}
              >
                <div className="hr-chatbot-message-content">
                  <p>{msg.text}</p>
                  <span className="hr-chatbot-timestamp">
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="hr-chatbot-message hr-message">
                <div className="hr-chatbot-typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} className="hr-chatbot-anchor" />
          </div>

          <div className="hr-chatbot-input-container">
            <div className="hr-chatbot-input-wrapper">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Type your message..."
                className="hr-chatbot-input"
              />
              <button onClick={sendMessage} className="hr-chatbot-send-btn">
                <i className="fas fa-paper-plane"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HRChatbot;